import React, { useEffect, useState } from 'react'
import {
    Grid,
    Box,
    Typography,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    IconButton,
    Tooltip
} from "@mui/material"
import PeopleIcon from '@mui/icons-material/People'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import RefreshIcon from '@mui/icons-material/Refresh'
import * as actions from "./actions"
import { toastError } from '../../utils'

export default function Dashboard(props) {
    const [analyticsData, setAnalyticsData] = useState({
        total_online_users: 0,
        online_users_list: [],
        pages: []
    })
    const [loading, setLoading] = useState(false)
    const [lastUpdated, setLastUpdated] = useState(null)

    useEffect(() => {
        fetchAnalytics()
        // Auto-refresh every 30 seconds
        const interval = setInterval(() => {
            fetchAnalytics()
        }, 30000)

        return () => clearInterval(interval)
    }, [])

    const fetchAnalytics = () => {
        setLoading(true)
        actions.getOnlineUsersAdmin()
            .then(res => {
                if (res.status === 200) {
                    setAnalyticsData(res.body)
                    setLastUpdated(new Date())
                }
                setLoading(false)
            })
            .catch(err => {
                toastError(err.message || 'Failed to fetch analytics')
                setLoading(false)
            })
    }

    const formatTime = (date) => {
        if (!date) return ''
        return date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        })
    }

    return (
        <Grid container spacing={3}>
            {/* Header */}
            <Grid item xs={12}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="h5" style={{ fontWeight: 600, color: '#333' }}>
                        Live Analytics Dashboard
                    </Typography>
                    <Box display="flex" alignItems="center" gap={2}>
                        {lastUpdated && (
                            <Typography variant="body2" color="textSecondary">
                                Last updated: {formatTime(lastUpdated)}
                            </Typography>
                        )}
                        <Tooltip title="Refresh">
                            <IconButton
                                onClick={fetchAnalytics}
                                disabled={loading}
                                size="small"
                                color="primary"
                            >
                                <RefreshIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
            </Grid>

            {/* Summary Card */}
            <Grid item xs={12} sm={6} md={4}>
                <Card elevation={3} style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                    <CardContent>
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Box>
                                <Typography variant="h3" style={{ color: '#fff', fontWeight: 700 }}>
                                    {analyticsData.total_online_users}
                                </Typography>
                                <Typography variant="body1" style={{ color: '#fff', marginTop: 8 }}>
                                    Users Online
                                </Typography>
                            </Box>
                            <PeopleIcon style={{ fontSize: 60, color: 'rgba(255,255,255,0.3)' }} />
                        </Box>
                    </CardContent>
                </Card>
            </Grid>

            {/* Online Users List */}
            <Grid item xs={12}>
                <Paper elevation={2}>
                    <Box p={3}>
                        <Typography variant="h6" style={{ marginBottom: 20, fontWeight: 600, color: '#333' }}>
                            Online Users ({analyticsData.online_users_list.length})
                        </Typography>
                        {analyticsData.online_users_list.length > 0 ? (
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow style={{ backgroundColor: '#f5f5f5' }}>
                                            <TableCell style={{ fontWeight: 600 }}>#</TableCell>
                                            <TableCell style={{ fontWeight: 600 }}>Name</TableCell>
                                            <TableCell style={{ fontWeight: 600 }}>Email</TableCell>
                                            <TableCell style={{ fontWeight: 600 }}>Uptime</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {analyticsData.online_users_list.map((user, index) => (
                                            <TableRow key={index} hover>
                                                <TableCell>{index + 1}</TableCell>
                                                <TableCell>{user.name}</TableCell>
                                                <TableCell>{user.email}</TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={user.uptime}
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <Box textAlign="center" py={5}>
                                <Typography variant="body1" color="textSecondary">
                                    No users currently online
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Paper>
            </Grid>

            {/* Page-wise Breakdown */}
            <Grid item xs={12}>
                <Paper elevation={2}>
                    <Box p={3}>
                        <Typography variant="h6" style={{ marginBottom: 20, fontWeight: 600, color: '#333' }}>
                            Page-wise User Distribution
                        </Typography>
                        {analyticsData.pages.length > 0 ? (
                            <Grid container spacing={2}>
                                {analyticsData.pages
                                    .sort((a, b) => b.active_users_count - a.active_users_count)
                                    .map((page, index) => (
                                        <Grid item xs={12} key={index}>
                                            <Accordion>
                                                <AccordionSummary
                                                    expandIcon={<ExpandMoreIcon />}
                                                    style={{
                                                        backgroundColor: page.active_users_count > 0 ? '#f0f7ff' : '#fafafa'
                                                    }}
                                                >
                                                    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                                                        <Typography style={{ fontWeight: 500 }}>
                                                            {page.page_name}
                                                        </Typography>
                                                        <Chip
                                                            label={`${page.active_users_count} ${page.active_users_count === 1 ? 'user' : 'users'}`}
                                                            color={page.active_users_count > 0 ? "primary" : "default"}
                                                            size="small"
                                                            style={{ marginRight: 10 }}
                                                        />
                                                    </Box>
                                                </AccordionSummary>
                                                <AccordionDetails>
                                                    {page.users.length > 0 ? (
                                                        <TableContainer>
                                                            <Table size="small">
                                                                <TableHead>
                                                                    <TableRow>
                                                                        <TableCell style={{ fontWeight: 600 }}>Name</TableCell>
                                                                        <TableCell style={{ fontWeight: 600 }}>Email</TableCell>
                                                                    </TableRow>
                                                                </TableHead>
                                                                <TableBody>
                                                                    {page.users.map((user, userIndex) => (
                                                                        <TableRow key={userIndex}>
                                                                            <TableCell>{user.name}</TableCell>
                                                                            <TableCell>{user.email}</TableCell>
                                                                        </TableRow>
                                                                    ))}
                                                                </TableBody>
                                                            </Table>
                                                        </TableContainer>
                                                    ) : (
                                                        <Typography variant="body2" color="textSecondary">
                                                            No users on this page
                                                        </Typography>
                                                    )}
                                                </AccordionDetails>
                                            </Accordion>
                                        </Grid>
                                    ))}
                            </Grid>
                        ) : (
                            <Box textAlign="center" py={5}>
                                <Typography variant="body1" color="textSecondary">
                                    No page data available
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </Paper>
            </Grid>
        </Grid>
    )
}
